import React, { useState } from "react";
import type { ApolloClient } from "@apollo/client";
import classnames from "classnames";
import { get, isEmpty, isNil, noop } from "lodash";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { FiDollarSign } from "react-icons/fi";
import { createLoadingSelector } from "store/selectors";
import { toast } from "react-toastify";
import { isEmployerSelector } from "store/selectors/user";
import {
  linkBank,
  linkBankIndividual,
  verifyBankAccount,
} from "actions/bankActions";
import {
  accountConstants,
  bankConstants,
  employerConstants,
} from "actions/types";
import {
  activeBanksSelector,
  bankSelector,
  isPendingManualBankVerification,
  isBankPending,
  isBankVerified,
} from "store/selectors/bank";
import { hasEnabled360PayrollIntegration } from "store/selectors/employer";
import type { RootState } from "types/state";
import type { UserBank } from "types/models";

import ActionCard from "components/ActionCard";
import PlaidLink from "components/PlaidLink";
import PayrollIntegratedACHDisclaimerModal from "pages/dashboards/employerDashboard/dashboard/PayrollIntegratedACHDisclaimerModal";

import "./AddBankCard.scss";

// The PlaidLink instance forwarded via its `render` prop; only handleOnClick is
// read here. propTypes only declared `object`.
interface PlaidInstance {
  handleOnClick?: () => void;
}

interface AddBankCardWithPlaidProps {
  plaidInstance?: PlaidInstance;
  bankAccount?: UserBank;
  hasBankAccount?: boolean;
  isAddingBank?: boolean;
  isEmployer?: boolean;
  isPendingAutoBankVerification?: boolean;
  isPendingManualBankVerification?: boolean;
  has360PayrollIntegration?: boolean;
}

const AddBankCardWithPlaid = ({
  plaidInstance = {},
  isAddingBank = false,
  isEmployer = false,
  bankAccount = {} as UserBank,
  has360PayrollIntegration = false,
}: AddBankCardWithPlaidProps) => {
  const [showACHDisclaimerModal, setShowACHDisclaimerModal] = useState(false);

  const isPending = isBankPending(bankAccount);
  const isActive = isBankVerified(bankAccount);
  const isPendingManualVerification =
    isPendingManualBankVerification(bankAccount);

  const cardWrapperClass = classnames("add-bank-card", {
    "action-card": !isActive && !isAddingBank,
    "completed-card": isActive,
  });

  const cardTitle = () => {
    if (isPending) {
      return "Bank Account is Being Verified";
    }
    return isEmployer ? "Link Company Bank Account" : "Link Your Bank Account";
  };

  const cardDescription = () => {
    if (isPendingManualVerification) {
      return "Verify the two small deposits in your account after one to two business days.";
    } else if (isPending) {
      return "Your bank account will be automatically verified in one to two business days.";
    }
    return isEmployer
      ? "Link your company bank account to fund plan payroll contributions."
      : "Link your bank account to start making contributions.";
  };

  const cardAction = () => {
    if (isPendingManualVerification) {
      return "Verify";
    }
    return "Link Bank";
  };

  const handleCardClick = () => {
    if (isEmployer && has360PayrollIntegration) {
      setShowACHDisclaimerModal(true);
    } else if (plaidInstance.handleOnClick) {
      plaidInstance.handleOnClick();
    }
  };

  const handleACHDisclaimerClick = () => {
    setShowACHDisclaimerModal(false);
    if (plaidInstance.handleOnClick) {
      plaidInstance.handleOnClick();
    }
  };

  return (
    <div className={cardWrapperClass}>
      <ActionCard
        onClick={handleCardClick}
        icon={<FiDollarSign color="white" stroke="#60A4BF" size={16} />}
        title={cardTitle()}
        description={cardDescription()}
        actionText={cardAction()}
        completedAction={
          (isActive || isPending) && !isPendingManualVerification
        }
        loading={isAddingBank}
      />
      <PayrollIntegratedACHDisclaimerModal
        show={showACHDisclaimerModal}
        onClose={() => setShowACHDisclaimerModal(false)}
        onClick={handleACHDisclaimerClick}
      />
    </div>
  );
};

const loadingSelector = createLoadingSelector([
  bankConstants.LINK_BANK_WITH_ICON,
  bankConstants.GET_PLAID_LINK_TOKEN,
  accountConstants.GET_ACCOUNTS,
  employerConstants.GET_EMPLOYER_ACCOUNTS,
]);

interface AddBankCardOwnProps {
  bankAccount?: UserBank;
  onSuccess?: (data: unknown) => void;
  insertOnlyMode?: boolean;
}

const getBankAccount = (state: RootState, props: AddBankCardOwnProps) => {
  /*
    This is necessary for multi-bank support for employers. Since we are displaying multiple add bank cards now,
    we need to pass in bank account from a list as a prop so we can render the correct state. This also maintains
    backwards compatibility for individuals by fetching whatever bank account exists in redux if none is passed as a prop
  */
  if (props.insertOnlyMode) {
    return {} as UserBank;
  }
  if (isEmpty(props.bankAccount)) {
    return bankSelector(state);
  }
  return props.bankAccount;
};

const mapStateToProps = (state: RootState, ownProps: AddBankCardOwnProps) => {
  const bankAccount = getBankAccount(state, ownProps);
  return {
    isEmployer: isEmployerSelector(state),
    hasVerifiedBankAccount: activeBanksSelector(state).length > 0,
    isAddingBank: loadingSelector(state),
    companyId: get(state.employer, "company.id", null),
    bankAccount,
    has360PayrollIntegration: hasEnabled360PayrollIntegration(state),
  };
};

const mapDispatchToProps = {
  linkBankIndividual,
  linkBank,
  verifyBankAccount,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<AddBankCardOwnProps & PropsFromRedux>;

// the dispatched link-bank thunk resolves to the SUCCESS/FAILURE action object
interface LinkBankResult {
  type: string;
  error?: unknown;
}

class AddBankCard extends React.PureComponent<Props> {
  static defaultProps = {
    insertOnlyMode: false,
    onSuccess: noop,
  };

  _handleOnSuccess = (token: string, metadata: unknown, ownerName: string) => {
    if (isPendingManualBankVerification(this.props.bankAccount)) {
      void this.props.verifyBankAccount(
        this.props.client as ApolloClient<object>,
        this.props.bankAccount!.id
      );
      return;
    }
    if (this.props.isEmployer) {
      void this.props
        .linkBank(
          this.props.client as ApolloClient<object>,
          token,
          metadata as Parameters<typeof linkBank>[2]
        )
        .then((data: LinkBankResult) => {
          if (data && isNil(data.error)) {
            toast.success("Successfully linked bank account.");
            this.props.onSuccess!(data);
          }
        });
    } else {
      void this.props
        .linkBankIndividual(
          this.props.client as ApolloClient<object>,
          token,
          metadata as Parameters<typeof linkBankIndividual>[2],
          ownerName
        )
        .then((data: LinkBankResult) => {
          if (data && isNil(data.error)) {
            toast.success("Successfully linked bank account.");
            this.props.onSuccess!(data);
          }
        });
    }
  };

  _noLinkedBankRender = (plaidInstance: React.Component) => {
    return (
      <AddBankCardWithPlaid
        plaidInstance={plaidInstance as unknown as PlaidInstance}
        bankAccount={this.props.bankAccount}
        isAddingBank={this.props.isAddingBank}
        isEmployer={this.props.isEmployer}
        has360PayrollIntegration={this.props.has360PayrollIntegration}
      />
    );
  };

  render() {
    if (
      isEmpty(this.props.bankAccount) ||
      isPendingManualBankVerification(this.props.bankAccount)
    ) {
      return (
        <PlaidLink
          // PlaidLink does not declare an isAddingBank prop — this was an inert
          // pass-through in the original JS; preserve it verbatim.
          // @ts-expect-error inert prop not declared on PlaidLink
          isAddingBank={this.props.isAddingBank}
          onSuccess={this._handleOnSuccess}
          render={this._noLinkedBankRender}
          bank={this.props.bankAccount}
        />
      );
    }

    return (
      <AddBankCardWithPlaid
        bankAccount={this.props.bankAccount}
        isAddingBank={this.props.isAddingBank}
        isEmployer={this.props.isEmployer}
        has360PayrollIntegration={this.props.has360PayrollIntegration}
      />
    );
  }
}

export default connector(withApollo<Props>(AddBankCard));
